// frontend/src/components/common/ImageUpload/ImageUploadHook.ts (Final - Media Feature)
import { useState, useCallback } from 'react';
import { httpClient } from '@/lib/httpClient';
import type { UseImageUploadOptions, ApiResponse } from '@/types/imageUpload';

export const useImageUpload = (options: UseImageUploadOptions = {}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadImages = useCallback(async (files: File[]) => {
    setUploading(true);
    setError(null);

    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('image', file);
        
        if (options.entityType) {
          formData.append('entity_type', options.entityType);
        }
        if (options.entityId) {
          formData.append('entity_id', String(options.entityId));
        }

        // Use the media feature endpoint
        const response = await httpClient.post(
          options.apiEndpoint || '/media/upload',
          formData
        ) as ApiResponse;

        if (response.success) {
          return response.data;
        } else {
          throw new Error(response.message || 'Upload failed');
        }
      });

      const results = await Promise.all(uploadPromises);
      return results;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setUploading(false);
    }
  }, [options]);

  const uploadSingleImage = useCallback(async (file: File) => {
    const results = await uploadImages([file]);
    return results[0];
  }, [uploadImages]);

  const deleteImage = useCallback(async (mediaId: string | number) => {
    try {
      const response = await httpClient.delete(`/media/delete/${mediaId}`) as ApiResponse;
      
      if (response.success) {
        return true;
      } else {
        throw new Error(response.message || 'Delete failed');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Delete failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const setPrimaryImage = useCallback(async (mediaId: string | number) => {
    try {
      const response = await httpClient.post(`/media/set-primary/${mediaId}`, {}) as ApiResponse;
      
      if (response.success) {
        return true;
      } else {
        throw new Error(response.message || 'Set primary failed');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Set primary failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const updateCaption = useCallback(async (mediaId: string | number, caption: string) => {
    try {
      const response = await httpClient.put(`/media/update/${mediaId}`, { caption }) as ApiResponse;
      
      if (response.success) {
        return true;
      } else {
        throw new Error(response.message || 'Caption update failed');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Caption update failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const getEntityImages = useCallback(async (entityType: string, entityId: string | number) => {
    try {
      const response = await httpClient.get(`/media/entity/${entityType}/${entityId}`) as ApiResponse;
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch images');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch images';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const getMediaStats = useCallback(async (entityType: string, entityId: string | number) => {
    try {
      const response = await httpClient.get(`/media/stats/${entityType}/${entityId}`) as ApiResponse;
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch stats');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch stats';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const bulkDelete = useCallback(async (mediaIds: (string | number)[]) => {
    try {
      const response = await httpClient.delete('/media/bulk-delete', { 
        data: { media_ids: mediaIds }
      }) as ApiResponse;
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Bulk delete failed');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Bulk delete failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  return {
    uploading,
    error,
    uploadImages,
    uploadSingleImage,
    deleteImage,
    setPrimaryImage,
    updateCaption,
    getEntityImages,
    getMediaStats,
    bulkDelete,
    clearError: () => setError(null),
  };
};

// Convenience hooks for specific use cases
export const useProviderImageUpload = (providerId: number) => {
  return useImageUpload({
    entityType: 'provider',
    entityId: providerId,
  });
};

export const useServiceImageUpload = (serviceId: number) => {
  return useImageUpload({
    entityType: 'service',
    entityId: serviceId,
  });
};

export const useUserAvatarUpload = (userId: number) => {
  return useImageUpload({
    entityType: 'user',
    entityId: userId,
  });
};

export const useCategoryIconUpload = (categoryId: number) => {
  return useImageUpload({
    entityType: 'category',
    entityId: categoryId,
  });
};

export const useBookingAttachmentUpload = (bookingId: number) => {
  return useImageUpload({
    entityType: 'booking',
    entityId: bookingId,
  });
};

// Generic hook for any entity type
export const useGenericImageUpload = (entityType: string, entityId: number | string) => {
  return useImageUpload({
    entityType,
    entityId,
  });
};